import { TFunction } from "i18next";
import { setToastMessage } from "../store/slices/app/appSlice";
import { Dispatch } from "@reduxjs/toolkit";
import { components } from "../types/api";
import { getPlatformDisplayName } from "./iconUtils";
import * as Sentry from "@sentry/react";

/**
 * Export publisher metrics data to CSV
 * @param publishers - Array of publisher metrics
 * @param campaignName - Name of the campaign
 * @param t - Translation function
 * @param dispatch - Redux dispatch function
 * @returns void
 */
export const exportPublisherMetricsToCSV = (
  publishers: components["schemas"]["PublisherMetricsSummary"][] | undefined,
  campaignName: string,
  t: TFunction,
  dispatch: Dispatch,
  currency: string
): void => {
  if (!publishers || publishers.length === 0) {
    // Show error toast if no data to export
    dispatch(
      setToastMessage({
        message: t("campaigns.noDataToExport", {
          defaultValue: "No data available to export",
        }),
        severity: "warning",
      })
    );
    return;
  }

  try {
    // Define all possible metrics columns and their corresponding property names
    const metricsColumns = [
      { header: "Publisher Name", property: "publisherName" },
      { header: "Platform", property: "platform" },
      { header: "Impressions", property: "impressions" },
      { header: "Clicks", property: "clicks" },
      { header: "CTR", property: "ctr" },
      { header: "CPM", property: "cpm" },
      { header: "CPC", property: "cpc" },
      { header: "Cost", property: "cost" },
      //{ header: "VCTR", property: "vctr" },
      //{ header: "VCPM", property: "vcpm" },
      { header: "Conversions", property: "conversions" },
      { header: "Conversion Rate", property: "conversionRate" },
      { header: "CPA", property: "cpa" },
      { header: "View Conversions", property: "viewConversions" },
      { header: "View Conversion Rate", property: "viewConversionRate" },
    ];

    // Create CSV header row
    const headers = metricsColumns.map((column) => column.header);

    // Function to escape CSV values (wrap in quotes if contains comma, newline or quote)
    const escapeCsvValue = (value: string | number): string => {
      const str = String(value);
      // If value contains comma, newline or quote, wrap in quotes and escape existing quotes
      if (str.includes(",") || str.includes("\n") || str.includes('"')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    // Create CSV rows from publishers data
    const rows = publishers.map((publisher) =>
      metricsColumns.map((column) => {
        let value: string | number = "";
        const rawValue = publisher[column.property as keyof typeof publisher];

        // Special handling for platform to use getPlatformDisplayName
        if (column.property === "platform" && publisher.platform) {
          value = getPlatformDisplayName(String(publisher.platform));
        } else if (rawValue !== null && rawValue !== undefined) {
          // Format numbers with commas and percentages appropriately
          if (typeof rawValue === "number") {
            if (
              column.property === "cpm" ||
              column.property === "cpc" ||
              column.property === "vcpm" ||
              column.property === "cost" ||
              column.property === "cpa"
            ) {
              value = currency + rawValue.toFixed(2);
            } else {
              value = rawValue.toString();
            }
          } else {
            value = String(rawValue);
          }
        }

        return escapeCsvValue(value);
      })
    );

    // Combine headers and rows
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    // Create a Blob with the CSV data, adding UTF-8 BOM for Excel compatibility
    // The BOM (Byte Order Mark) helps Excel detect UTF-8 encoding correctly
    const BOM = new Uint8Array([0xef, 0xbb, 0xbf]);
    const blob = new Blob([BOM, csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    // Create a download link and trigger the download
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `publisher_metrics_${campaignName || "export"}_${
        new Date().toISOString().split("T")[0]
      }.csv`
    );
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Show success toast
    dispatch(
      setToastMessage({
        message: t("campaigns.exportSuccess", {
          defaultValue: "Metrics data exported successfully",
        }),
        severity: "success",
      })
    );
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error exporting publisher metrics data:", error);

    // Show error toast
    dispatch(
      setToastMessage({
        message: t("campaigns.exportError", {
          defaultValue: "Error exporting metrics data",
        }),
        severity: "error",
      })
    );
  }
};
